import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Field, useForm } from 'react-final-form'
import cn from 'classnames'
import { useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import styles from './index.module.scss'
import useQuery from '../../hooks/useQuery'
import QUERY_KEYS from '../../graphql/queryKeys'
import termsAndConditionsQuery from '../../graphql/queries/termsAndConditions'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import { AGREEMENT_INFO_TIP, NO_TC_AGREEMENT_VALUE, SIGNATURE_TRIGGER_HELPER_TEXT } from '../../pages/create-terms-and-condition-agreement/constants'

const NONE_OPTION = { value: NO_TC_AGREEMENT_VALUE, label: 'No Agreement Selected' }

export function hasPublishedVersion (agreement) {
  if (!agreement) return false
  if (agreement.currentPublishedVersion?.id) return true
  const latest = agreement.latestVersion
  return Boolean(latest && (latest.status === 'published' || latest.publishedAt))
}

export default function TCAgreementInput ({ onAgreementTypeChange, assignedAgreementId }) {
  const form = useForm()
  const hasAppliedDefault = useRef(false)
  const haulerId = useSelector(({ user }) => user.user?.haulerId)
  const { data: { termsAndConditions } = {}, isFetched } = useQuery(
    [QUERY_KEYS.termsAndConditions, { haulerId, isEnabled: true, forAssignment: true }],
    termsAndConditionsQuery,
    {
      enabled: Boolean(haulerId),
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve T&C agreements')
      },
      placeholderData: { termsAndConditions: [] }
    }
  )

  const assignedId = assignedAgreementId && assignedAgreementId !== NO_TC_AGREEMENT_VALUE
    ? String(assignedAgreementId)
    : null

  const agreementOptions = useMemo(() => {
    const options = (termsAndConditions || [])
      .filter((agreement) => (
        hasPublishedVersion(agreement) ||
        (assignedId && String(agreement.id) === assignedId)
      ))
      .map((agreement) => ({
        value: String(agreement.id),
        label: agreement.title,
        signatureTrigger: agreement.signatureTrigger
      }))
    return [NONE_OPTION, ...options]
  }, [termsAndConditions, assignedId])

  useEffect(() => {
    ReactTooltip.rebuild()
  }, [])

  useEffect(() => {
    // Create only: edit already has assignedAgreementId (id or 'none') in form initial values.
    if (!isFetched || hasAppliedDefault.current || assignedAgreementId !== undefined) return

    const defaultAgreement = (termsAndConditions || [])
      .filter(hasPublishedVersion)
      .find(agreement => agreement.isDefault)
    const value = defaultAgreement ? String(defaultAgreement.id) : NONE_OPTION.value

    form.change('tcAgreementType', value)
    if (onAgreementTypeChange) onAgreementTypeChange(value)
    hasAppliedDefault.current = true
  }, [isFetched, termsAndConditions, form, onAgreementTypeChange, assignedAgreementId])

  return (
    <div className={styles.tcAgreementContainer}>
      <div className={cn('font-size-1-5em', styles.inputSectionTitle)}>
        Terms and Conditions
        <span
          className={styles.infoIconWrap}
          data-tip={AGREEMENT_INFO_TIP}
          data-for='tc-agreement-info-tooltip'>
          <i className={cn('material-icons', 'dis-info-icon', styles.infoIconSize)}>
            info
          </i>
        </span>
      </div>

      <label className='control-label' htmlFor='tcAgreementType'>Select a Terms and Conditions template to use for this Account.
        If no agreement is selected, Terms and Conditions will not apply.</label>

      <Field name='tcAgreementType'>
        {({ input }) => {
          const selected = agreementOptions.find((opt) => opt.value === input.value)
          const helperText = selected?.signatureTrigger
            ? SIGNATURE_TRIGGER_HELPER_TEXT[selected.signatureTrigger]
            : null

          return (
            <>
              <select
                {...input}
                id='tcAgreementType'
                className={styles.tcAgreementSelect}
                onChange={(e) => {
                  input.onChange(e)
                  onAgreementTypeChange && onAgreementTypeChange(e.target.value)
                }}>
                {agreementOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className={styles.helperText}>
                {helperText || '*Select a T&C agreement to assign to this account, or choose No Agreement.'}
              </p>
            </>
          )
        }}
      </Field>
      <ReactTooltip
        id='tc-agreement-info-tooltip'
        place='bottom'
        effect='solid'
        multiline
        className={styles.tooltip}
      />
    </div>
  )
}

TCAgreementInput.propTypes = {
  onAgreementTypeChange: PropTypes.func,
  assignedAgreementId: PropTypes.string
}
